import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowRight,
  Star,
  Zap,
  TrendingUp,
  Mic,
  ChevronDown,
  ChevronUp,
  Quote,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';

const HERO_SLIDES = [
  {
    id: 1,
    image:
      'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Design Your Destiny',
    subtitle:
      'Stop settling for an ordinary life. Unlock the psychology of success and rewire your mind for exponential growth.',
    cta: 'Start Now',
    align: 'left'
  },
  {
    id: 2,
    image:
      'https://images.unsplash.com/photo-1553877522-43269d4ea984?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Master Your Business',
    subtitle:
      'Multyply your sales, organize your systems, and build a legacy of financial freedom.',
    cta: 'Scale Now',
    align: 'center'
  },
  {
    id: 3,
    image:
      'https://images.unsplash.com/photo-1552581234-26160f608093?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80',
    title: 'Unleash Inner Power',
    subtitle:
      'Break through anxiety, fear, and limiting beliefs using neuroscience, proven scientific methods, and spiritual intelligence.',
    cta: 'Learn More',
    align: 'left'
  }
];

const TESTIMONIALS = [
  {
    id: 1,
    name: 'Rajesh Verma',
    role: 'Insurance Agency Leader',
    text: "I was stuck at the same revenue for 3 years. The 'Special Growth Mastery' program completely rewired my approach. My team doubled our MDRT qualifiers in just 6 months.",
    image:
      'https://images.unsplash.com/photo-1566492031773-4f4e44671857?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 2,
    name: 'Priya Sharma',
    role: 'Entrepreneur',
    text: "Exiser isn't just motivation; it's pure strategy. The psychological frameworks helped me overcome imposter syndrome and close high-ticket deals with total confidence.",
    image:
      'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  },
  {
    id: 3,
    name: 'Amit Patel',
    role: 'Sales Professional',
    text: "The 'Limitless Mind' retreat changed my life. I broke through fears I didn't even know I had. My performance at work has skyrocketed.",
    image:
      'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&auto=format&fit=crop&w=200&q=80'
  }
];

const FAQS = [
  {
    question: 'Is this just another motivational course?',
    answer:
      "No. Motivation is temporary. We teach strategy and psychology. We rewire the patterns in your brain that cause you to fail, ensuring lasting change rather than a temporary 'high'. Our approach combines neuroscience, sales mastery, and spiritual intelligence."
  },
  {
    question: 'Do I need to attend live events?',
    answer:
      'While our live events are immersive experiences that accelerate results, our 1:1 coaching and online curriculums are designed to be effective from anywhere in the world. We also offer Zoom-based group training.'
  },
  {
    question: 'How quickly will I see results?',
    answer:
      "Most clients report a shift in perspective immediately. Tangible results in business revenue and personal clarity typically compound within the first 30-90 days of consistent application of our 'Success Formula'."
  }
];

const Home = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [activeAccordion, setActiveAccordion] = useState(0);
  const [currentTestimonial, setCurrentTestimonial] = useState(0);

  // Auto-play hero slider
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length);
    }, 6000);
    return () => clearInterval(timer);
  }, []);

  // Auto-play testimonials
  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentTestimonial(prev => (prev + 1) % TESTIMONIALS.length);
    }, 8000);
    return () => clearInterval(timer);
  }, []);

  const nextSlide = () =>
    setCurrentSlide(prev => (prev + 1) % HERO_SLIDES.length);
  const prevSlide = () =>
    setCurrentSlide(prev => (prev - 1 + HERO_SLIDES.length) % HERO_SLIDES.length);

  return (
    <div className="flex flex-col bg-exiser-light overflow-x-hidden">
      {/* HERO SLIDER SECTION */}
      <div className="relative h-[85vh] md:h-[90vh] w-full overflow-hidden bg-exiser-dark clip-diagonal group">
        {HERO_SLIDES.map((slide, index) => (
          <div
            key={slide.id}
            className={`absolute inset-0 transition-opacity duration-1000 ease-in-out ${index === currentSlide ? 'opacity-100 z-10' : 'opacity-0 z-0'
              }`}
          >
            {/* Background Image with Ken Burns Effect */}
            <div
              className={`absolute inset-0 w-full h-full transition-transform duration-[8000ms] ease-linear ${index === currentSlide ? 'scale-110' : 'scale-100'
                }`}
            >
              <img
                src={slide.image}
                alt={slide.title}
                className="w-full h-full object-cover opacity-60 mix-blend-overlay"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-exiser-primary via-exiser-primary/80 to-transparent" />
            </div>

            {/* Text Content */}
            <div className="relative z-20 h-full max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center">
              <div
                className={`max-w-3xl transition-all duration-1000 ${slide.align === 'center' ? 'mx-auto text-center' : ''
                  }`}
              >
                <div
                  className={`flex items-center gap-2 mb-6 ${slide.align === 'center' ? 'justify-center' : ''
                    } transition-all duration-700 delay-100 transform ${index === currentSlide
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-10 opacity-0'
                    }`}
                />

                <h1
                  className={`text-5xl md:text-8xl font-bold text-white leading-[0.9] mb-8 uppercase tracking-tight drop-shadow-2xl transition-all duration-700 delay-300 transform ${index === currentSlide
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                    }`}
                >
                  {slide.title.split(' ').map((word, i) => (
                    <span
                      key={i}
                      className={
                        i === 1
                          ? 'text-transparent bg-clip-text bg-gradient-to-r from-exiser-accent to-yellow-200'
                          : ''
                      }
                    >
                      {word} <br className="hidden md:inline" />
                    </span>
                  ))}
                </h1>

                <p
                  className={`font-sans text-xl text-purple-100 mb-10 max-w-xl leading-relaxed drop-shadow-md transition-all duration-700 delay-500 transform ${index === currentSlide
                    ? 'translate-y-0 opacity-100'
                    : 'translate-y-10 opacity-0'
                    }`}
                >
                  {slide.subtitle}
                </p>

                <div
                  className={`flex flex-col sm:flex-row gap-4 ${slide.align === 'center' ? 'justify-center' : ''
                    } transition-all duration-700 delay-700 transform ${index === currentSlide
                      ? 'translate-y-0 opacity-100'
                      : 'translate-y-10 opacity-0'
                    }`}
                >
                  {/* Primary CTA → /book */}
                  <Link
                    to="/book"
                    className="inline-flex items-center justify-center px-10 py-5 bg-exiser-accent text-exiser-primary font-oswald font-bold text-xl uppercase tracking-wider hover:bg-white hover:text-exiser-primary transition-all shadow-[0_0_20px_rgba(250,204,21,0.6)] hover:-translate-y-1 transform skew-x-[-10deg] border-2 border-exiser-accent"
                  >
                    <span className="skew-x-[10deg]">{slide.cta}</span>
                  </Link>

                  {/* Secondary CTA → /events */}
                  <Link
                    to="/events"
                    className="inline-flex items-center justify-center px-10 py-5 bg-transparent border-2 border-white text-white font-oswald font-bold text-xl uppercase tracking-wider hover:bg-white hover:text-exiser-primary transition-all transform skew-x-[-10deg]"
                  >
                    <span className="skew-x-[10deg]">View Events</span>
                  </Link>
                </div>
              </div>
            </div>
          </div>
        ))}

        {/* Slider Controls */}
        <button
          onClick={prevSlide}
          className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-2 text-white/50 hover:text-exiser-accent hover:bg-black/20 rounded-full transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronLeft className="w-12 h-12" />
        </button>
        <button
          onClick={nextSlide}
          className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-2 text-white/50 hover:text-exiser-accent hover:bg-black/20 rounded-full transition-all opacity-0 group-hover:opacity-100"
        >
          <ChevronRight className="w-12 h-12" />
        </button>

        {/* Slider Indicators */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 z-30 flex gap-3">
          {HERO_SLIDES.map((_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentSlide(idx)}
              className={`h-1 transition-all duration-300 ${idx === currentSlide
                ? 'w-12 bg-exiser-accent'
                : 'w-6 bg-white/30 hover:bg-white'
                }`}
            />
          ))}
        </div>
      </div>

      {/* PRODUCTS SECTION */}
      <div className="py-24 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 uppercase mb-4">
            Pathways To Mastery
          </h2>
          <div className="w-24 h-2 bg-exiser-accent mx-auto mb-6" />
          <p className="text-gray-600 max-w-2xl mx-auto font-sans text-lg">
            We don't just host events or sessions. We deliver transformation. Choose your path to
            greatness below.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {/* Card 1 */}
          <Link
            to="/corporate-training"
            className="group bg-white rounded-none shadow-lg hover:shadow-2xl transition-all duration-300 border border-gray-100 flex flex-col h-full hover:-translate-y-2 relative overflow-hidden"
          >
            <div className="absolute top-0 left-0 w-1 h-full bg-exiser-accent transform -translate-x-full group-hover:translate-x-0 transition-transform duration-300" />
            <div className="h-64 overflow-hidden relative">
              <div className="absolute top-4 left-4 bg-exiser-primary text-white text-xs font-bold px-3 py-1 uppercase z-10">
                Best Seller
              </div>
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Business"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-exiser-primary/20 group-hover:bg-transparent transition-colors" />
            </div>
            <div className="p-8 flex-1 flex flex-col">
              <h3 className="text-2xl font-bold uppercase text-gray-900 mb-3 group-hover:text-exiser-primary transition-colors">
                Corporate & Growth
              </h3>
              <p className="text-gray-500 font-sans mb-6 text-sm leading-relaxed">
                Systemize your business, explode your sales, and master leadership. Perfect for
                Insurance Professionals and Agency Leaders.
              </p>
              <div className="mt-auto pt-6 border-t border-gray-100 flex items-center justify-between">
                <span className="text-lg font-bold text-gray-900">Consultancy</span>
                <span className="text-exiser-primary font-bold text-sm uppercase flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                  Learn More <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </div>
          </Link>

          {/* Card 2 */}
          <Link
            to="/counselling"
            className="group bg-exiser-primary rounded-none shadow-2xl transition-all duration-300 border border-exiser-primary flex flex-col h-full transform md:scale-105 z-10 hover:-translate-y-3 relative overflow-hidden ring-4 ring-white/10"
          >
            <div className="absolute -right-12 top-8 bg-exiser-accent text-exiser-primary text-xs font-bold px-12 py-1 uppercase z-20 rotate-45 shadow-md animate-pulse">
              Most Popular
            </div>
            <div className="h-64 overflow-hidden relative">
              <img
                src="https://images.unsplash.com/photo-1544717305-2782549b5136?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                alt="Mindset"
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
              />
              <div className="absolute inset-0 bg-black/40 group-hover:bg-transparent transition-colors" />
            </div>
            <div className="p-8 flex-1 flex flex-col text-white relative">
              <h3 className="text-2xl font-bold uppercase text-white mb-3 flex items-center gap-2">
                Life & Mindset Mastery{' '}
                <Star className="w-5 h-5 text-exiser-accent fill-current" />
              </h3>
              <p className="text-purple-200 font-sans mb-6 text-sm leading-relaxed">
                The ultimate 1:1 coaching package. Rewire your brain for success, remove limiting
                beliefs, and build unstoppable confidence.
              </p>
              <div className="mt-auto pt-6 border-t border-purple-800 flex items-center justify-between">
                <span className="text-lg font-bold text-exiser-accent">Application Only</span>
                <span className="bg-exiser-accent text-exiser-primary px-6 py-2 font-bold text-sm uppercase shadow-lg group-hover:bg-white group-hover:text-exiser-primary transition-colors">
                  Get Started
                </span>
              </div>
            </div>
          </Link>
        </div>
      </div>

      {/* TESTIMONIALS SLIDER */}
      <div className="bg-exiser-light py-20 border-y border-gray-200">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <Quote className="w-16 h-16 text-exiser-accent mx-auto mb-8 opacity-50" />
          <h2 className="text-3xl font-bold uppercase mb-12 text-exiser-primary">
            Success Stories
          </h2>

          <div className="relative min-h-[300px]">
            {TESTIMONIALS.map((testimonial, idx) => (
              <div
                key={testimonial.id}
                className={`absolute inset-0 transition-all duration-700 transform flex flex-col items-center justify-center ${idx === currentTestimonial
                  ? 'opacity-100 translate-x-0 scale-100 blur-0'
                  : 'opacity-0 translate-x-12 scale-95 blur-sm pointer-events-none'
                  }`}
              >
                <p className="text-2xl md:text-3xl font-sans text-gray-800 italic leading-relaxed mb-8 max-w-3xl">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center gap-4 bg-white p-4 rounded-full shadow-lg border border-purple-50">
                  <img
                    src={testimonial.image}
                    alt={testimonial.name}
                    className="w-16 h-16 rounded-full border-2 border-exiser-accent object-cover"
                  />
                  <div className="text-left">
                    <div className="font-bold text-gray-900 uppercase">
                      {testimonial.name}
                    </div>
                    <div className="text-xs tracking-wider text-exiser-primary font-bold uppercase">
                      {testimonial.role}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-center gap-2 mt-8">
            {TESTIMONIALS.map((_, idx) => (
              <button
                key={idx}
                onClick={() => setCurrentTestimonial(idx)}
                className={`w-3 h-3 rounded-full transition-all duration-300 ${idx === currentTestimonial
                  ? 'bg-exiser-primary w-8'
                  : 'bg-gray-300 hover:bg-exiser-accent'
                  }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* WHY CHOOSE US */}
      <div className="bg-exiser-primary text-white py-24 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-pulse" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-exiser-accent rounded-full mix-blend-multiply filter blur-3xl opacity-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <div>
              <h2 className="text-4xl md:text-5xl font-bold uppercase mb-6 leading-tight">
                The <span className="text-exiser-accent">Exiser</span>
                <br />
                Difference
              </h2>
              <p className="font-sans text-lg text-purple-200 mb-8">
                Most coaching is just therapy. We don't do therapy. We do strategy. We uncover the
                specific patterns holding you back and give you the tools to smash through them
                immediately.
              </p>
              <ul className="space-y-6">
                <li className="flex items-center gap-4 group">
                  <div className="bg-white/10 p-3 rounded-full text-exiser-accent group-hover:bg-exiser-accent group-hover:text-exiser-primary transition-colors">
                    <Zap className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold uppercase">Rapid Reprogramming</h4>
                    <p className="text-sm text-purple-300 font-sans group-hover:text-white transition-colors">
                      See changes in thinking patterns instantly.
                    </p>
                  </div>
                </li>
                <li className="flex items-center gap-4 group">
                  <div className="bg-white/10 p-3 rounded-full text-exiser-accent group-hover:bg-exiser-accent group-hover:text-exiser-primary transition-colors">
                    <TrendingUp className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold uppercase">Sales & Growth Science</h4>
                    <p className="text-sm text-purple-300 font-sans group-hover:text-white transition-colors">
                      Proven systems for professionals and agencies.
                    </p>
                  </div>
                </li>
                <li className="flex items-center gap-4 group">
                  <div className="bg-white/10 p-3 rounded-full text-exiser-accent group-hover:bg-exiser-accent group-hover:text-exiser-primary transition-colors">
                    <Mic className="w-6 h-6" />
                  </div>
                  <div>
                    <h4 className="text-xl font-bold uppercase">Immersive Events</h4>
                    <p className="text-sm text-purple-300 font-sans group-hover:text-white transition-colors">
                      Environments designed to shift your state permanently.
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="relative group">
              <div className="border-8 border-exiser-accent p-2 transform rotate-3 bg-white transition-transform duration-500 group-hover:rotate-6 shadow-2xl">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80"
                  alt="Exiser Team Difference"
                  className="w-full grayscale contrast-125 group-hover:grayscale-0 transition-all duration-500"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* FAQ SECTION */}
      <div className="py-24 max-w-4xl mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold uppercase text-gray-900">Common Questions</h2>
          <div className="w-24 h-1 bg-exiser-accent mx-auto mt-4" />
        </div>

        <div className="space-y-4">
          {FAQS.map((faq, index) => (
            <div
              key={index}
              className="border border-gray-200 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-lg hover:border-exiser-primary/30 bg-white"
            >
              <button
                onClick={() =>
                  setActiveAccordion(activeAccordion === index ? null : index)
                }
                className="w-full flex items-center justify-between p-6 text-left focus:outline-none bg-white hover:bg-gray-50 transition-colors"
              >
                <span
                  className={`text-xl font-bold uppercase transition-colors ${activeAccordion === index ? 'text-exiser-primary' : 'text-gray-900'
                    }`}
                >
                  {faq.question}
                </span>
                {activeAccordion === index ? (
                  <ChevronUp className="w-6 h-6 text-exiser-accent" />
                ) : (
                  <ChevronDown className="w-6 h-6 text-gray-400" />
                )}
              </button>
              <div
                className={`transition-all duration-500 ease-in-out overflow-hidden ${activeAccordion === index
                  ? 'max-h-96 opacity-100'
                  : 'max-h-0 opacity-0'
                  }`}
              >
                <div className="p-6 pt-2 font-sans text-gray-600 leading-relaxed border-t border-gray-100 bg-gray-50">
                  {faq.answer}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* SUCCESS BANNER */}
      <div className="bg-exiser-accent py-16 relative overflow-hidden group">
        <div className="absolute inset-0 bg-white/20 translate-y-full group-hover:translate-y-0 transition-transform duration-700" />
        <div className="max-w-7xl mx-auto px-4 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold text-exiser-primary uppercase mb-8 leading-tight">
            "The Best Investment You Will
            <br />
            Ever Make Is In Yourself"
          </h2>
          <Link
            to="/book"
            className="inline-block bg-exiser-primary text-white font-bold px-12 py-5 text-lg uppercase tracking-widest hover:bg-white hover:text-exiser-primary transition-all shadow-xl hover:scale-105 border-2 border-exiser-primary"
          >
            Book a Session
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Home;